/*
 * Prescription Upload Form Component with Zod Validation
 */

import React, { useState } from 'react';
import {
  View,
  Heading,
  Text,
  TextField,
  Button,
  Flex,
  ProgressCircle,
  Well,
  Divider,
  DatePicker,
  Content
} from '@adobe/react-spectrum';
import UploadToCloud from '@spectrum-icons/workflow/UploadToCloud';
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle';
import Alert from '@spectrum-icons/workflow/Alert';
import { z } from 'zod';
import { parseDate } from '@internationalized/date';
import { uploadFile, submitPrescription, fileToBase64 } from '../utils/api';
import { usePrescriptions } from '../hooks/usePrescriptions';
import { EMAIL_REGEX, NAME_MIN_LENGTH } from '../utils/validation';
import { isEncryptedPdf, sha256Hex } from '../utils/fileChecks';

// Zod validation schema (email uses the shared regex/message so all forms match)
const prescriptionSchema = z.object({
  email: z.string().refine((v) => EMAIL_REGEX.test((v || '').trim()), 'Please enter a valid email address'),
  name: z.string().trim().min(NAME_MIN_LENGTH, `Name must be at least ${NAME_MIN_LENGTH} characters`),
  dob: z.string().min(1, 'Date of birth is required'),
  orderNumber: z.string().optional()
});

export function PrescriptionUploadForm({ customerData, onSuccess, onCancel, ims, quoteId, quoteSkus }) {

  // console.log('Quote Id in PrescriptionUploadForm:', quoteId);
  // console.log('Quote SKU in PrescriptionUploadForm:', quoteSkus);

  const { save, data: prescriptionsData } = usePrescriptions(customerData, ims);
  const [formData, setFormData] = useState({
    email: customerData?.email || '',
    name: `${customerData?.firstname || ''} ${customerData?.lastname || ''}`.trim() || '',
    dob: customerData?.dob || '',
    orderNumber: ''
  });

  const [dateValue, setDateValue] = useState(() => {
    if (customerData?.dob) {
      try {
        return parseDate(customerData.dob);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileHash, setFileHash] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDateChange = (value) => {
    setDateValue(value);
    const dobString = value ? value.toString() : '';
    setFormData(prev => ({
      ...prev,
      dob: dobString
    }));
    if (errors.dob) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.dob;
        return newErrors;
      });
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];

    // Reset any previously accepted file until the new one passes every check.
    setSelectedFile(null);
    setFileHash(null);

    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ file: 'Invalid file type. Please upload a PDF or image file (JPG, PNG, GIF).' });
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors({ file: 'File size exceeds 100MB limit.' });
      return;
    }

    try {
      // Reject password-protected / encrypted PDFs (staff can't open them).
      if (await isEncryptedPdf(file)) {
        setErrors({ file: 'This PDF is password-protected. Please upload an unlocked copy.' });
        return;
      }

      // Reject a file the customer has already uploaded (same content hash).
      // NetSuite returns `params` as a JSON string, so parse it defensively;
      // tolerate a couple of legacy shapes too.
      const hash = await sha256Hex(file);
      const existing = prescriptionsData?.prescriptions || [];
      const parseParams = (p) => {
        const raw = p?.params;
        if (!raw) return null;
        if (typeof raw === 'object') return raw;
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
          return null;
        }
      };
      const storedHash = (p) => parseParams(p)?.hash ?? p?.hash ?? p?.file_hash;
      if (existing.some((p) => storedHash(p) === hash)) {
        setErrors({ file: "You've already uploaded this prescription file." });
        return;
      }
      setFileHash(hash);
    } catch (err) {
      setErrors({ file: 'Could not read the selected file. Please try again.' });
      return;
    }

    setSelectedFile(file);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.file;
      return newErrors;
    });
  };

  const validateForm = () => {
    const fieldErrors = {};
    if (!formData.email || formData.email.trim() === '') {
      fieldErrors.email = 'Email is required';
    }

    if (!formData.name || formData.name.trim() === '') {
      fieldErrors.name = 'Name is required';
    }

    if (!formData.dob || formData.dob.trim() === '') {
      fieldErrors.dob = 'Date of birth is required';
    }

    if (!selectedFile) {
      fieldErrors.file = 'Please select a prescription file to upload';
    }

    try {
      prescriptionSchema.parse(formData);
    } catch (error) {
      // Zod v4 exposes issues on `error.issues` (`error.errors` was the v3 name);
      // fall back to `errors` so this survives a Zod downgrade.
      const issues = error instanceof z.ZodError ? (error.issues ?? error.errors) : null;
      if (Array.isArray(issues)) {
        issues.forEach(err => {
          if (!fieldErrors[err.path[0]]) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return false;
    }

    return true;
  };

  const handleBlur = (field) => {
    const value = formData[field];

    if (field === 'email' || field === 'name' || field === 'dob') {
      if (!value || value.trim() === '') {
        setErrors(prev => ({
          ...prev,
          [field]: `This field is required`
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });

    setSuccess(false);


    if (!validateForm()) {
      return;
    }

    setUploading(true);

    try {
      // Step 1: Validate raw file size (S3 also enforces this via the presigned POST policy)
      const maxSizeMB = 20;
      if (selectedFile.size > maxSizeMB * 1024 * 1024) {
        throw new Error(`File too large. Maximum size is ${maxSizeMB}MB.`);
      }

      // Step 2: Upload the file directly to S3 via a presigned POST
      setUploadProgress('Uploading file...');
      const uploadResponse = await uploadFile(
        selectedFile,
        selectedFile.name,
        formData.email,
        ims
      );

      if (!uploadResponse.success || !uploadResponse.file) {
        throw new Error('File upload failed');
      }

      // Step 3: Submit prescription data to NetSuite
      setUploadProgress('Saving prescription data...');
      const prescriptionPayload = {
        customerEmail: formData.email,
        customerName: formData.name,
        dob: formData.dob,
        orderNumber: formData.orderNumber || null,
        fileUrl: uploadResponse.file.url,
        fileName: uploadResponse.file.name,
        fileId: uploadResponse.file.id,
        fileSize: uploadResponse.file.size,
        fileHash: fileHash,
        quoteId: quoteId,
        quoteSkus: quoteSkus,
      };

      const saveResponse = await save.mutateAsync({
        ...prescriptionPayload,
        ims
      });

      if (!saveResponse.success) {
        setErrors({ submit: saveResponse.error || saveResponse.message });
        setUploading(false);
        return;
      }

      // Success!
      setSuccess(true);
      setUploadProgress('Prescription submitted successfully!');

      // Call success callback and reset after delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);

    } catch (err) {
      // console.error('Error submitting prescription:', err);
      setErrors({ submit: err.message || 'Failed to submit prescription. Please try again.' });
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };


  return (
    <View padding="size-200" width="100%">
      <Flex
        direction="column"
        gap="size-300"
        alignItems="center"
      >
        <View width="100%" maxWidth="size-6000" marginTop="size-600">
          <Flex direction="column" >
            <Heading level={2}>Upload Prescription</Heading>
            <Divider size="S" />
            <Content marginTop="size-200">
              <form onSubmit={handleSubmit}>
                <Flex direction="column" gap="size-200">
                  <TextField
                    label="Email"
                    value={formData.email}
                    onChange={(value) => handleInputChange('email', value)}
                    isRequired
                    type="email"
                    width="100%"
                    isDisabled={uploading}
                    validationState={errors.email ? 'invalid' : 'valid'}
                    errorMessage={errors.email}
                  />
                  <TextField
                    label="Full Name"
                    value={formData.name}
                    onChange={(value) => handleInputChange('name', value)}
                    onBlur={() => handleBlur('name')}
                    isRequired
                    width="100%"
                    isDisabled={uploading}
                    validationState={errors.name ? 'invalid' : 'valid'}
                    errorMessage={errors.name}
                  />
                  <Flex direction="column" gap="size-100" width="100%">
                    <Text UNSAFE_style={{ fontSize: '12px', color: '#3e3c3cff' }}>
                      Date of Birth *
                    </Text>
                    <input
                      id="dob"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => handleInputChange('dob', e.target.value)}
                      onBlur={() => handleBlur('dob')}
                      max={new Date().toISOString().split('T')[0]}
                      disabled={uploading}
                      required
                      style={{
                        height: '30px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: errors.dob ? '2px solid #d7373f' : '1px solid #ccc',
                        outline: 'none'
                      }}
                    />
                    {errors.dob && (
                      <Text UNSAFE_style={{ fontSize: '13px', color: '#d7373f' }}>
                        {errors.dob}
                      </Text>
                    )}
                  </Flex>
                  <TextField
                    label="Order Number (Optional)"
                    value={formData.orderNumber}
                    onChange={(value) => handleInputChange('orderNumber', value)}
                    onBlur={() => handleBlur('orderNumber')}
                    width="100%"
                    isDisabled={uploading}
                  />
                  <Flex direction="column" gap="size-100">
                    <Text>
                      <strong>Prescription File (PDF or Image) *</strong>
                    </Text>
                    <Flex direction="column" gap="size-100">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.gif"
                        onChange={handleFileSelect}
                        disabled={uploading}
                        style={{ display: 'none' }}
                        id="file-input"
                      />
                      <label htmlFor="file-input" style={{ width: '100%' }}>
                        <Button
                          variant="secondary"
                          onPress={() => document.getElementById('file-input').click()}
                          isDisabled={uploading}
                          width="100%"
                        >
                          <UploadToCloud />
                          <Text>Choose File</Text>
                        </Button>
                      </label>
                      {selectedFile && (
                        <Text UNSAFE_style={{ fontSize: '13px', color: '#0a0a0a', wordBreak: 'break-word' }}>
                          Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Text>
                      )}
                      {errors.file && (
                        <Text UNSAFE_style={{ fontSize: '13px', color: '#d7373f' }}>
                          {errors.file}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  {uploading && (
                    <Well>
                      <Flex direction="row" gap="size-200" alignItems="center">
                        <ProgressCircle aria-label="Uploading" isIndeterminate size="S" />
                        <Text>{uploadProgress}</Text>
                      </Flex>
                    </Well>
                  )}
                  {success && (
                    <Well>
                      <Flex direction="row" gap="size-200" alignItems="center">
                        <CheckmarkCircle color="positive" size="S" />
                        <Text>
                          <strong>Success!</strong> Your prescription has been submitted.
                        </Text>
                      </Flex>
                    </Well>
                  )}
                  {errors.submit && (
                    <Well>
                      <Flex direction="row" gap="size-200" alignItems="center">
                        <Alert color="negative" size="S" />
                        <Text>
                          <strong>Error:</strong> {errors.submit}
                        </Text>
                      </Flex>
                    </Well>
                  )}
                  <Flex direction="row" gap="size-200" marginTop="size-200" justifyContent="end">
                    <Button
                      variant="secondary"
                      onPress={onCancel}
                      isDisabled={uploading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="cta"
                      onPress={handleSubmit}
                      isDisabled={uploading || !selectedFile}
                    >
                      {uploading ? 'Submitting...' : 'Submit Prescription'}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Content>
          </Flex>
        </View>
      </Flex>
    </View>
  );
}